import { useState, useMemo, useCallback } from 'react';
import { Form } from '../../../../components';
import { getAzureLocalCluster, getGiBfromMiB, handleError } from '../../../../lib/helpers';
import { useNotificationContext } from '../../../../providers';
import { IP_TYPE, NIC_TYPE_ETHERNET } from '../../../../constants/network';
import { useCloudAttributes } from '../hooks/useCloudAttributes';
import { useApplianceRedeployPrefill } from './useApplianceRedeployPrefill';
import { buildSourceCloudsPayload } from '../components/VMBasedMigrationsConfig/helpers';
import { useApplianceCommonState } from '../components/CommonApplianceConfiguration/useApplianceCommonState';

const NETWORK_FIELD_RESET = {
    subnet: undefined,
    ipType: IP_TYPE.DHCP,
    ipAddr: '',
    netmask: '',
    gateway: '',
    primaryDns: '',
    secondaryDns: '',
};

const buildAzureLocalApplianceNetworkPayload = (values, subnet = {}) => {
    const ipType = values.ipType || IP_TYPE.DHCP;
    const isStatic = ipType === IP_TYPE.STATIC;

    const eth0 = {
        ip_type: ipType,
        type: NIC_TYPE_ETHERNET,
        network_name: subnet.name,
        dv_switch_name: '',
    };
    if (isStatic) {
        eth0.ip_addr = values.ipAddr || '';
        eth0.netmask = values.netmask || '';
    }

    const payload = { interfaces: { eth0 } };
    if (isStatic) {
        if (values.gateway) {
            payload.routes = { default_gateway: values.gateway.trim() };
        }
        if (values.primaryDns || values.secondaryDns) {
            payload.dns = {
                primary: (values.primaryDns || '').trim(),
                secondary: (values.secondaryDns || '').trim(),
            };
        }
    }
    return payload;
};

export const useAzureLocalApplianceSetup = ({ cloudAccount, onSubmit, isRedeploy }) => {
    const [form] = Form.useForm();
    const { notifyError } = useNotificationContext();

    const [subscriptions, setSubscriptions] = useState([]);
    const [customLocations, setCustomLocations] = useState([]);
    const [resourceGroups, setResourceGroups] = useState([]);
    const [storagePaths, setStoragePaths] = useState([]);
    const [logicalNetworks, setLogicalNetworks] = useState([]);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [instanceTags, setInstanceTags] = useState({});
    const [loadingLocationData, setLoadingLocationData] = useState(false);

    const subscription = Form.useWatch('subscription', form);
    const location = Form.useWatch('location', form);
    const virtualNetwork = Form.useWatch('virtualNetwork', form);

    const {
        useProxyEnabled,
        migrationInstructions,
        ignoreValidationErrors,
        commonConfigProps,
        prefillCommonFromAppliance,
    } = useApplianceCommonState(form);

    const handleAttributesLoaded = (result) => {
        if (result?.properties?.subscriptions) {
            setSubscriptions(result.properties.subscriptions);
        }
    };

    const { initialLoading, loadingError, loadCloudAttributes, fetchAttributes, loadingAttributes } =
        useCloudAttributes(cloudAccount, handleAttributesLoaded);

    const loadSubscriptionDetails = useCallback(
        async (subscriptionId) => {
            setLoadingLocationData(true);
            try {
                const result = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { subscription_id: subscriptionId },
                }).unwrap();

                const subscriptionData = result?.properties?.subscriptions?.[0];
                if (subscriptionData) {
                    setSubscriptionDetails(subscriptionData);
                    setCustomLocations(subscriptionData.custom_locations || []);
                    setResourceGroups(subscriptionData.resource_groups || []);
                }
                return subscriptionData;
            } catch (error) {
                handleError(error, notifyError, 'Failed to load subscription details');
                return null;
            } finally {
                setLoadingLocationData(false);
            }
        },
        [cloudAccount, fetchAttributes, notifyError],
    );

    useApplianceRedeployPrefill({
        isRedeploy,
        cloudAccount,
        subscriptions,
        form,
        loadSubscriptionDetails,
        prefillCommonFromAppliance,
        setStoragePaths,
        setLogicalNetworks,
        setInstanceTags,
    });

    const handleSubscriptionChange = useCallback(
        (subscriptionId) => {
            setCustomLocations([]);
            setResourceGroups([]);
            setStoragePaths([]);
            setLogicalNetworks([]);
            setSubscriptionDetails(null);
            form.setFieldsValue({
                location: undefined,
                storagePath: undefined,
                resourceGroup: undefined,
                virtualNetwork: undefined,
                ...NETWORK_FIELD_RESET,
            });
            if (subscriptionId) {
                loadSubscriptionDetails(subscriptionId);
            }
        },
        [form, loadSubscriptionDetails],
    );

    const handleLocationChange = useCallback(
        (locationId) => {
            setStoragePaths([]);
            setLogicalNetworks([]);
            form.setFieldsValue({ storagePath: undefined, virtualNetwork: undefined, ...NETWORK_FIELD_RESET });

            if (!subscriptionDetails || !locationId) {
                return;
            }
            const selectedLocation = locationId.toLowerCase();
            setStoragePaths(
                (subscriptionDetails.storage_paths || []).filter(
                    (storagePath) => (storagePath.custom_location || '').toLowerCase() === selectedLocation,
                ),
            );
            setLogicalNetworks(
                (subscriptionDetails.logical_networks || []).filter(
                    (network) => (network.location_id || '').toLowerCase() === selectedLocation,
                ),
            );
        },
        [form, subscriptionDetails],
    );

    const handleVirtualNetworkChange = useCallback(() => {
        form.setFieldsValue({ ...NETWORK_FIELD_RESET });
    }, [form]);

    const selectedLogicalNetwork = useMemo(() => {
        const [vnetName, resourceGroup] = (virtualNetwork || '').split('|');
        return logicalNetworks.find((network) => network.name === vnetName && network.resource_group === resourceGroup);
    }, [virtualNetwork, logicalNetworks]);

    const handleFormSubmit = useCallback(
        (values) => {
            const [vnetName, vnetResourceGroup] = (values.virtualNetwork || '').split('|');
            const locationObject = customLocations.find((location) => location.id === values.location);
            const storagePathObject = storagePaths.find((storagePath) => storagePath.id === values.storagePath);
            const network = logicalNetworks.find(
                (network) => network.name === vnetName && network.resource_group === vnetResourceGroup,
            );
            const cluster = getAzureLocalCluster(subscriptionDetails, values.resourceGroup);
            const subnet = network?.subnets?.[0];

            const cloudProperties = {
                subscription_id: values.subscription,
                resource_group: values.resourceGroup,
                region: locationObject?.location || '',
                custom_location: storagePathObject?.custom_location || '',
                storage_path: values.storagePath,
                virtual_network: { name: vnetName, resource_group: vnetResourceGroup },
                tags: instanceTags || {},
                network: buildAzureLocalApplianceNetworkPayload(values, subnet),
                vapp_name: values.vmName?.trim() || undefined,
                cluster: cluster?.name,
                proxy_type: 'none',
            };
            const payload = {
                cloud_properties: cloudProperties,
                vm_level_migration_enabled: !!values.vmLevelEnabled,
                ignore_validation_errors: ignoreValidationErrors,
                ca_proxy: useProxyEnabled,
                migration_instructions: migrationInstructions,
            };

            if (values.vmLevelEnabled) {
                payload.source_clouds = buildSourceCloudsPayload(values);
            }

            onSubmit(payload);
        },
        [
            customLocations,
            storagePaths,
            logicalNetworks,
            instanceTags,
            ignoreValidationErrors,
            useProxyEnabled,
            migrationInstructions,
            onSubmit,
        ],
    );

    const subscriptionOptions = useMemo(
        () => subscriptions.map((subscription) => ({ value: subscription.id, label: subscription.name })),
        [subscriptions],
    );

    const customLocationOptions = useMemo(
        () => customLocations.map((location) => ({ value: location.id, label: `${location.name} (${location.location})` })),
        [customLocations],
    );

    const storagePathOptions = useMemo(
        () =>
            storagePaths.map((storagePath) => {
                const availableGiB = getGiBfromMiB(storagePath.available_size_in_mb);
                const totalGiB = getGiBfromMiB(storagePath.container_size_in_mb);
                return { value: storagePath.id, label: `${storagePath.name} ( ${availableGiB} GiB of ${totalGiB} GiB available )` };
            }),
        [storagePaths],
    );

    const logicalNetworkOptions = useMemo(
        () =>
            logicalNetworks.map((network) => ({
                value: `${network.name}|${network.resource_group}`,
                label: `${network.name} | ${network.resource_group}`,
            })),
        [logicalNetworks],
    );

    return {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingAttributes,
        loadingLocationData,
        subscription,
        location,
        virtualNetwork,
        resourceGroups,
        selectedLogicalNetwork,
        instanceTags,
        setInstanceTags,
        handleSubscriptionChange,
        handleLocationChange,
        handleVirtualNetworkChange,
        handleFormSubmit,
        subscriptionOptions,
        customLocationOptions,
        storagePathOptions,
        logicalNetworkOptions,
        commonConfigProps,
    };
};

